import { computed, ref } from 'vue'

import { isAuthRedirectError } from '@/api/request'
import { projectTimelineApi } from '@/api/projectTimeline'
import type { ProjectTimelineItem, ProjectTimelineResponse } from '@/types/projectTimeline'

interface UseProjectTimelineOptions {
  projectId: number
  showLoadError: (message: string) => void
}

export function useProjectTimeline(options: UseProjectTimelineOptions) {
  const timeline = ref<ProjectTimelineResponse | null>(null)
  const loading = ref(false)
  let inFlightRequest: Promise<void> | null = null
  let refreshRequestedWhileLoading = false

  async function loadProjectTimeline({ showError = true, silent = false }: { showError?: boolean; silent?: boolean } = {}) {
    if (inFlightRequest) {
      refreshRequestedWhileLoading = true
      return inFlightRequest
    }

    const shouldShowLoading = !silent && !timeline.value
    if (shouldShowLoading) {
      loading.value = true
    }

    inFlightRequest = (async () => {
      try {
        timeline.value = await projectTimelineApi.getByProjectId(options.projectId)
      } catch (error) {
        if (isAuthRedirectError(error)) {
          return
        }
        if (showError) {
          options.showLoadError('加载项目时间线失败')
        }
      } finally {
        if (shouldShowLoading) {
          loading.value = false
        }
      }
    })()

    try {
      await inFlightRequest
    } finally {
      inFlightRequest = null
      if (refreshRequestedWhileLoading) {
        refreshRequestedWhileLoading = false
        await loadProjectTimeline({ showError, silent: true })
      }
    }
  }

  const items = computed<ProjectTimelineItem[]>(() => timeline.value?.items || [])
  const createdAt = computed(() => timeline.value?.created_at || null)
  const latestUpdatedAt = computed(() => timeline.value?.latest_updated_at || null)

  return {
    projectTimeline: timeline,
    projectTimelineItems: items,
    projectTimelineCreatedAt: createdAt,
    projectTimelineLatestUpdatedAt: latestUpdatedAt,
    projectTimelineLoading: loading,
    loadProjectTimeline,
  }
}